import angular from 'angular';
import { vrmConfigModule } from '@/angular/modules/vrm-config';
import * as Sentry from "@sentry/browser";

export const vrmApiModule = angular.module('vrm.api', [
    vrmConfigModule.name,
]);

vrmApiModule
    .service("ApiService", ApiService);

/**
 * @constructor
 * @ngInject
 */
function ApiService($rootScope, vrmConfig, $log, $http, $q, vrmNotificationsService, vrmNotificationType, jwtHelper, localStorageService, locale) {

    /* Public */
    var service = {
        auth,
        verifyShare,
        users: users,
        deleteUserAccount: deleteUserAccount,
        admin: admin,
        adminWithAbort: adminWithAbort,
        installationDataAttributes: installationDataAttributes,
        token: _rawToken,
        dashboard: dashboard,
        diagnostics: diagnostics,
        installations: installations,
        recurringReports: recurringReports,
        reportTemplates: reportTemplates,
        installationSettings: installationSettings,
        essSettings: essSettings,
        installationDownload: installationDownload,
        firmwareLibrary: firmwareLibrary,
        whatsnewChangelog: whatsnewChangelog,
        logRemoteControl: logRemoteControl,
        download: download,
        call: call,
        call2,
        formatErrors: formatErrors,
        isLoggedIn: isLoggedIn,
        isShare: isShare,
        getUserId,
        clearAlarm,
        verifyEmbed,
        getAlarmLogs,
        getInstallationLogs,
        getSitePreferences,
        updateSitePreferences,
        getCacheLocation,
        getInstallationSearchResults,
        addTag,
        removeTag,
        installationUploadPhoto,
        scheduleDynamicEss,
        simulateHistoricEss,
        getUserPreferences,
        hideBanner,
        setRemoteConsolePreference,
        setOpenedRemoteConsoleVersion,
        getDynamicEssSettings,
        venusGrafana,
        hideTwoFABanner,
        getSiteSearchHistory,
        setSiteSearchHistory,
        getAttributes,
        addAlarmsInBulk,
        updateFavoriteInstallation,
        firmwareRelease,
        deleteFirmwareRelease,
        createNewFirmwareRelease,
        editFirmwareRelease,
        firmwares,
        uploadFirmwares,
        deviceNames,
        errorMutedPaths: new Set(), // Set of regex strings
    };

    /* Private */

    return service;

    /* methods */


    /**
     * Util to format errors messages into a single single
     * @param response
     * @returns {*}
     */
    function formatErrors(response) {
        var result = "";
        if (response.data.error_code && response.data.error_code == "invalid_token") {
            result = locale.getString("core.invalid_token");
        }
        else if (!_.isObject(response.data.errors) && response.data && response.data.errors) {
            result = response.data.errors;
        }
        else if (response.data && response.data.error_code && response.data.error_code == "validation_error" && response.data.errors && response.data.errors.constructor.name == "Object") {
            _.each(response.data.errors, function (errorMessage, fieldName) {
                // convert fieldName from camelCase to space separated words. eg. "totalEstimatedBatteryCycles" to "total estimated battery cycles"
                fieldName = fieldName.replace(/([A-Z])/g, ' $1');
                // if fieldName is underscored, replace underscores with spaces. eg. "current_password" to "current password"
                fieldName = fieldName.replace(/_/g, ' ');
                // If fieldName is an array, get the last element. eg. "users[0][email]" to "email"
                fieldName = fieldName.replace(/.*\[(.*)\].*/, '$1');
                // Capitalize the first letter of the fieldName
                fieldName = fieldName.trim().toLowerCase().replace(/^\w/, c => c.toUpperCase());

                // if fieldName is numeric, just show the error message
                if (/^\d+$/.test(fieldName)) {
                    result += errorMessage + "<br>";
                    return;
                }

                result += `${locale.getString("core.api_validation_error", { fieldName, errorMessage })}<br>`;
            });
        }
        else if (_.isObject(response.data.errors)) {
            result = _.reduce(response.data.errors, function (result, message) {
                result += message + "<br>";
                return result;
            }, "");
        }
        else {
            result = locale.getString("core.unknown_error");
        }
        return result;
    }


    /**
     *
     * @param path
     * @param data
     */
    function users(path, data, skipResultChecks) {
        if (!_token()) {
            var promise = $q.defer();
            promise.reject("Unauthorized");
            return promise.promise;
        }
        return call('/users/' + getUserId() + '/' + path, data, undefined, undefined, undefined, skipResultChecks);
    }

    function deleteUserAccount(data) {
        if (!_token()) {
            var promise = $q.defer();
            promise.reject("Unauthorized");
            return promise.promise;
        }
        return call('/users/' + _token().uid + '/delete', data, 'DELETE', undefined, undefined, undefined);
    }

    function diagnostics(siteId, params) {
        return call('/installations/' + siteId + '/diagnostics', params, 'GET');
    }

    function dashboard(siteId, params) {
        return installations(siteId, 'dashboard', params, 'GET');
    }

    function admin(path, params, method, skipResultChecks) {
        method = method === undefined ? 'GET' : method;

        // catch AbortError
        return call('/admin/' + path, params, method, undefined, undefined, skipResultChecks);
    }

    async function adminWithAbort(newAbortSignal, path, params, method, responseType) {
        var token = localStorageService.get("token");

        const options = {
            method: "GET",
            // headers: {...headers},
            responseType: 'application/json',
            // skipAuthorization,
            headers: { "X-Authorization": `Bearer ${token}` },
            signal: newAbortSignal
        };
        const response = await fetch(`${vrmConfig.api}/admin/${path}` + new URLSearchParams(params), options);
        const data = await response.json()
        return { data: data }; // to follow format of other table services
    }

    function installationDataAttributes(query, page) {
        return call2('/admin/installation-data-attributes', { query: query, page: page }, { method: "GET" });
    }

    /**
     * @param id
     * @param path
     * @param data
     * @param method
     */
    function installations(id, path, data, method) {
        if (method == undefined) method = 'GET';
        return call('/installations/' + id + '/' + path, data, method);
    }

    function installationsReturnError(id, path, data, method) {
        if (method == undefined) method = 'GET';
        return call('/installations/' + id + '/' + path, data, method, false, 'json', true);
    }

    function recurringReports(siteId, recurringReportId, data, method) {
        var path = 'recurring-reports/' + (recurringReportId || '');
        return installations(siteId, path, data, method);
    }

    function reportTemplates(siteId, reportTemplateId, data, method) {
        var path = 'report-templates/' + (reportTemplateId || '');
        return installationsReturnError(siteId, path, data, method);
    }



    /**
     * Save settings for installation and show success notification after save
     * @param id of installation
     * @param data
     * @returns {promise}
     */
    function installationSettings(id, data, showNotification) {
        if (showNotification == undefined) showNotification = true;

        if (data.notes) {
            var notesUpdated = true;
        }
        return $q.all([call('/installations/' + id + '/settings', data, 'POST'), locale.ready('graph')]).then(function (result) {
            if (showNotification) {

                if (notesUpdated) {
                    vrmNotificationsService.add({
                        type: vrmNotificationType.SUCCESS,
                        message: locale.getString('core.notes_saved')
                    });
                }
                else {
                    vrmNotificationsService.add({
                        type: vrmNotificationType.SUCCESS,
                        message: locale.getString('core.settings_saved')
                    });
                }
            }
            return result[0];
        });
    }

    /**
     * Save ESS settings for installation and show success notification after save
     * @param id of installation
     * @param data
     * @param showNotification
     * @returns {promise}
     */
    function essSettings(id, data, showNotification, updateExisting) {
        if (showNotification == undefined) showNotification = true;
        let method = updateExisting ? 'PATCH' : 'POST';
        return $q.all([call('/installations/' + id + '/dynamic-ess-settings', data, method), locale.ready('graph')]).then(function (result) {
            if (showNotification) {
                vrmNotificationsService.add({
                    type: vrmNotificationType.SUCCESS,
                    message: locale.getString('core.settings_saved')
                });
            }
            return result[0];
        });
    }

    /**
     * Request blob for downloading file from installation
     * @param id
     * @param path
     * @param data
     */
    function installationDownload(id, path, data, skipResultChecks) {
        return download('/installations/' + id + '/' + path, data, skipResultChecks);
    }

    function whatsnewChangelog() {
        return call('/whatsnew-changelog', null, 'GET', true);
    }

    function logRemoteControl(id, data) {
        return call2(`/installations/${id}/remote-controls`, data, { skipResultChecks: true })
    }

    function firmwareLibrary(data, params) {
        return call2(`/admin/firmware-upgrade`, data, params)
    }

    function scheduleDynamicEss(id) {
        return call2(`/installations/${id}/schedule-dynamic-ess`, null, { skipResultChecks: true })
    }

    function simulateHistoricEss(id, configuration = null) {
        const method = configuration === null ? 'GET' : 'POST';
        return call2(
          `/installations/${id}/simulate-historic-ess`,
          configuration,
          {
              method,
              skipResultChecks: true,
              throwOnError: true,
          }
        );
    }

    /**
     * Endpoint for auth calls
     *
     * @param path
     * @param data
     * @param skipAuthorization
     * @returns {promise}
     */
    function auth(path, data) {
        return call('/auth' + path, data, 'POST', true);
    }

    function verifyShare(data) {
        const onApiError = (response, promise) => {
            if (response.data.error_code === 'password_required') {
                vrmNotificationsService.add({ type: vrmNotificationType.INFO, message: formatErrors(response) });
                return promise.reject(response.data.error_code);
            } else {
                return handleApiError(response, promise);
            }
        };
        return call2('/auth/verifyshare', data, { onApiError });
    }

    /**
     * Request blob for downloading file
     * @param path
     * @param data
     * @param showErrorNotification
     */
    function download(path, data, skipResultChecks = false, method = 'GET') {
        return call2(path, data, {
            method: method,
            responseType: 'blob',
            skipResultChecks: skipResultChecks,
            throwOnError: true,
        });
    }

    /**
     * @deprecated Use call2 instead
     * @param path The API Path
     * @param data The payload
     * @param method The method
     * @param skipAuthorization whether authorization must be skipped
     * @param responseType
     * @param skipResultChecks
     * @returns promise
     */
    function call(path, data, method, skipAuthorization, responseType, skipResultChecks) {
        return call2(path, data, { method, skipAuthorization, responseType, skipResultChecks });
    }

    function call2(path, data, {
        method = 'POST',
        skipAuthorization = false,
        skipResultChecks = false,
        responseType = 'json',
        onApiError = handleApiError,
        headers = {},
        throwOnError = !skipResultChecks,
    } = {}) {
        // password_required
        const promise = $q.defer();

        if (!window.navigator.onLine) {
            promise.reject();
            return promise.promise;
        }

        var successCallback = function (response) {
            if (skipResultChecks) return promise.resolve(response);
            handleResponse(response);
            if (response.data && 'success' in response.data && !response.data.success) {
                return onApiError(response, promise);
            }
            else {
                return promise.resolve(response);
            }
        };
        var errorCallback = function (error) {
            // Handle case when errors are cancelled on page change
            if (error.status === -1 && error.config.isManualCancellation) {
                // Blank return to prevent throwing an error
                return;
            }
            if (!skipResultChecks) {
                handleResponse(error);
                handleError(error, path);
            }
            if (throwOnError) {
                return promise.reject(error);
            } else {
                return promise.reject();
            }
        };

        var pattern = /^((http|https):\/\/)/;
        const options = {
            method,
            url: pattern.test(path) ? path : vrmConfig.api + path,
            headers: { ...headers },
            responseType: responseType,
            skipAuthorization,
        };
        if (method == 'POST' || method == 'DELETE' || method == 'PUT' || method == 'PATCH') {
            options.data = data;
            options.headers['Content-Type'] = data instanceof FormData ? undefined : 'application/json';
            options.processData = false;
        } else {
            options.params = data;
        }

        $http(options).then(successCallback, errorCallback);
        return promise.promise;
    }

    function handleResponse(response) {
        //Refresh token if new one available
        var refreshToken = response.headers ? response.headers('X-Token') : null;
        if (refreshToken != null) {
            $log.info("Got new token, saving it");
            localStorageService.set("token", refreshToken);
            $rootScope.$broadcast('tokenChange', refreshToken);
        }
    }

    /**
     * Global error handler, also passthrough
     * @param error
     */
    function handleError(error, path) {
        if (!window.navigator.onLine || error.status == -1) {
            return;
        }

        // If the token is invalid, remove it locally
        if (error.data && error.data.error_code && error.data.error_code === "invalid_token") {
            localStorageService.remove("token")
        }

        if ([...service.errorMutedPaths].some(mutedPath => new RegExp(mutedPath).test(path))){
            // Mute error for this path
            return
        }

        // Add an notification of the error
        if (error.data && error.data.message) {
            window.dataLayer.push({ event: 'gtag.box_error', status: error.status, path, value: 'error_with_data_message' });
            vrmNotificationsService.add({ type: vrmNotificationType.DANGER, message: error.data.message + ' (1511)' }, false)
        }
        else if (error.data && error.data.errors) {
            window.dataLayer.push({ event: 'gtag.box_error', status: error.status, path, value: 'error_with_data_errors' });
            locale.ready('core').then(function () {
                vrmNotificationsService.add({ type: vrmNotificationType.DANGER, message: formatErrors(error) + ' (1512)' }, false)
            });
        }
        else if (error.status === 429) {
            window.dataLayer.push({ event: 'gtag.box_error', status: error.status, path, value: 'rate_limiting_error' });
            // Rate limiting error
            locale.ready('core').then(function () {
                vrmNotificationsService.add({ type: vrmNotificationType.DANGER, message: locale.getString("core.rate_limiting_error") + " (1429)" });
            });
        }
        else if (error.status === 504 || error.status === 502) {
            window.dataLayer.push({ event: 'gtag.box_error', status: error.status, path, value: 'gateway_timeout_error' });
            // Gateway timeout
            locale.ready('core').then(function () {
                vrmNotificationsService.add({ type: vrmNotificationType.DANGER, message: locale.getString("core.gateway_timeout_error") + " (1504)"  });
            });
        }
        else if (error.statusText && error.statusText != "") {
            if (error.statusText === "Internal Server Error") {
                window.dataLayer.push({ event: 'gtag.box_error', status: error.status, path, value: 'backend_unavailable' });
                locale.ready('core').then(function () {
                    vrmNotificationsService.add({ type: vrmNotificationType.ERROR, message: locale.getString("core.backend_unavailable") + " (1500)" });
                });
            }
            else {
                window.dataLayer.push({ event: 'gtag.box_error', status: error.status, path, value: 'error_with_status_text' });
                vrmNotificationsService.add({ type: vrmNotificationType.DANGER, message: error.statusText + " (1510)" }, true);
            }
        }
        else {
            window.dataLayer.push({ event: 'gtag.box_error', status: error.status, path, value: 'unknown_error' });
            vrmConfig.sentry === true && Sentry.withScope(function (scope) {
                scope.setLevel('info');
                scope.setTag("error_type", 'unknown_error');
                scope.setFingerprint(['unknown_error']);
                Sentry.captureException(new Error(`Request ${path} failed with status ${error.status}`));
            });
            locale.ready('core').then(function () {
                vrmNotificationsService.add({ type: vrmNotificationType.DANGER, message: locale.getString("core.unknown_error") + " (1520)" });
            });
        }
        // Log error
        if (vrmConfig.debug)
            $log.error('API error', error);
    }

    function handleApiError(response, promise) {
        if (response.data.errors) {
            locale.ready('core').then(function () {
                vrmNotificationsService.add({ type: vrmNotificationType.WARNING, message: formatErrors(response) }, true);
            });
            promise.reject(response.data.error_code);
            // Log error
            if (vrmConfig.debug)
                $log.error('API error', response.data.error_code);
        } else if (response.data && ('error_message_code' in response.data)) {
            // Pass external errors (eg. mqtt-rpc, celery, redis) to be handled in the component
            if (vrmConfig.debug)
                $log.error('External error', response.data.error_message_code);
            promise.reject(response);
        } else if (response.data && response.data.error && response.data.duplicates) {
            promise.reject(response);
        } else {
            locale.ready('core').then(function () {
                vrmNotificationsService.add({
                    type: vrmNotificationType.ERROR,
                    message: locale.getString("core.unknown_error")
                });
            });
            promise.reject("Errors");
            // Log error
            if (vrmConfig.debug)
                $log.error('API error', "Unknown error");
        }
    }

    function getTokenFromLocalStorage() {
        if (new URLSearchParams(window.location.search).has('demoShare')) {
            return vrmConfig.demoShareToken;
        }
        return localStorageService.get("token");
    }

    /**
     * Simple passthrough function
     * @param response
     * @param promise
     */

    function _parseToken(data) {
        return data;
    }

    function _rawToken() {
        var token = getTokenFromLocalStorage();
        return token ? token : null;
    }

    /**
     * _token
     * @private
     */
    function _token() {
        var token = getTokenFromLocalStorage();
        return token ? _parseToken(jwtHelper.decodeToken(token)) : null;
    }

    function isShare() {
        var token = _token();
        if (token) {
            return token.share;
        }
        return false;
    }

    function getUserId() {
        const token = _token();
        return token ? token.uid : null;
    }

    /**
     * Check if token is available
     * @returns {boolean}
     */
    function isLoggedIn() {
        var token = getTokenFromLocalStorage();
        return token ? !jwtHelper.isTokenExpired(token) : false;
    }

    function clearAlarm(installationId, data) {
        return call('/installations/' + installationId + '/clear-alarm/', data, 'POST');
    }

    /**
     * Verify if embed token is valid
     * @param data
     */
    function verifyEmbed(data) {
        const onApiError = (response, promise) => {
            return handleApiError(response, promise);
        };
        return call2('/auth/verifyembed', data, { onApiError });
    }

    function getAlarmLogs(installationId, data) {
        return call2(`/installations/${installationId}/alarm-log`, data, {
            method: 'GET',
            skipResultChecks: true,
        }).then(response => response.data.alarms ? response.data.alarms : []);
    }

    function getInstallationLogs(installationId, data) {
        return call2(`/installations/${installationId}/installation-log`, data, {
            method: 'GET',
            skipResultChecks: true,
        }).then(response => response.data.events ? response.data.events : []);
    }

    function getCacheLocation(installationId){
        return call2(`/installations/${installationId}/cache-location`, null, {
            method: 'GET',
            skipResultChecks: true,
        }).then(response => response && response.data)
    }

    function getInstallationSearchResults(search, isExtended = false) {
        return call2(`/users/${_token().uid}/search?query=${search}${isExtended ? "&extended=1" : ""}`, null, {
            method: 'GET',
        }).then(response => response && response.data)
    }

    function setRemoteConsolePreference(installationId, choice) {
        return call2('/installations/' + installationId + '/remote-console-preferences',
            { "choice": choice,},
            {method: 'PUT'});
    }

    function setOpenedRemoteConsoleVersion(installationId, version) {
        return call2('/installations/' + installationId + '/remote-console', {
            console_version: version,
        })
    }

    function getSitePreferences(installationId) {
        return call2(`/installations/${installationId}/site-preferences`, null, {
            method: 'GET',
        }).then(response => response && response.data)
    }

    function updateSitePreferences(installationId, data) {
        return call2(`/installations/${installationId}/site-preferences`, data, {
            method: 'PUT',
            body: data,
        }).then(response => response && response.data)
    }

    function addTag(installationId, data) {
        return call2(`/installations/${installationId}/tags`, data, {
            method: 'PUT',
        }).then(function (result) {
                    vrmNotificationsService.add({
                        type: vrmNotificationType.SUCCESS,
                        message: locale.getString('core.settings_saved')
                    });
            return result[0];
        });
    }

    function removeTag(installationId, data) {
        return call2(`/installations/${installationId}/tags`, data, {
            method: 'DELETE',
        }).then(function (result) {
            vrmNotificationsService.add({
                type: vrmNotificationType.SUCCESS,
                message: locale.getString('core.settings_saved')
            });
            return result[0];
        });
    }

    function getDynamicEssSettings(id, skipResultChecks = true) {
        return call2(`/installations/${id}/dynamic-ess-settings`, null, {
            method: "GET",
            skipResultChecks: skipResultChecks
        }).then(function(response) {
            return response.data;
        });
    }

    function installationUploadPhoto(installationId, data) {
        return call2(`/installations/${installationId}/photo-upload`, data)
            .then(function (response) {
                vrmNotificationsService.add({
                    type: vrmNotificationType.SUCCESS,
                    message: locale.getString('dashboard.photo_uploaded')
                });
                return response.data;
            });
    }

    function getUserPreferences() {
        if (getUserId() !== null) {
            return call2(`/users/${getUserId()}/user-preferences`,
                null,
                { method: 'GET' }
            ).then(response => response && response.data.userPreferences);
        }
    }

    function hideBanner(){
        const updatedUserPreferences = {
            hideWatchAppsDashboardBanner: 1, // Hide the dashboard banner
        };
        return call2(`/users/${getUserId()}/user-preferences`, updatedUserPreferences, {
            method: 'PUT',
        });

    }

    function venusGrafana(userGroupId, method, data = {}){
        return call2(`/users/groups/${userGroupId}/venus-grafana`, {}, {
            method
        });
    }

    function hideTwoFABanner(){
        const updatedUserPreferences = {
            hide2FABanner: 1, // Hide the banner.
        };
        return call2(`/users/${getUserId()}/user-preferences`, updatedUserPreferences, {
            method: 'PUT',
        });

    }

    function getAttributes(installationId) {
        return call2(`/installations/${installationId}/attributes`, null, {
            skipResultChecks: true,
        })
            .then(response => response && response.data && response.data.result)
            .catch(() => {
                vrmNotificationsService.add({
                    type: vrmNotificationType.ERROR,
                    message: locale.getString('errors.error_loading_data', {
                        i18nFallback: 'There was an error loading the data, please try again.',
                    }),
                });
            });
    }

    function getSiteSearchHistory(siteId, type) {
        return call2(`/installations/${siteId}/search-history?type=${type}`, null, {
            method: 'GET',
        }).then(response => response && response.data)
    }

    function setSiteSearchHistory(siteId, type, data) {
        return call2(`/installations/${siteId}/search-history?type=${type}`, data, {
            method: 'PUT',
        })
    }

    /**
     * Add alarm rules in bulk to multiple installations
     * @param {number} installationId - Source installation ID
     * @param {Object} data - Request payload
     * @param {number[]} data.siteIds - Target installation IDs (excluding source)
     * @param {Object} data.alarmData - Device and alarm settings:
     *   Common: {idSite, instance, idDataAttribute}
     *   Float type: {...common, lowAlarm, lowAlarmHysteresis, highAlarm, highAlarmHysteresis, NotifyAfterSeconds, AlarmEnabled}
     *   Enum type: {...common, enumValues[], notifyAfterSeconds, alarmEnabled}
     * @param {boolean} [applyToSameSubtype] - Only apply to matching device subtypes
     * @returns {Promise<{existingAlarms: [], invalidAlarms: []}>}
     */
    function addAlarmsInBulk (installationId, data, applyToSameSubtype = false) {
        return call2(`/installations/${installationId}/bulk-alarms?applyToSameSubtype=${applyToSameSubtype}`, data, {
        })
    }

    /**
     * Toggles the favorite status for the given siteId
     * @param siteId - Site id of the installation to toggle
     * @param isFavorite - {boolean} New status, favorite or not.
     */
    function updateFavoriteInstallation(siteId, isFavorite) {
        return call2(`/installations/${siteId}/favorite`, {
            favorite: isFavorite ? 1 : 0,
        });
    }

    function firmwareRelease(idRelease) {
        return call2(`/firmware-release${!!idRelease ? "?idRelease="+idRelease : ""}`, null, {
            method: 'GET',
        });
    }

    function deleteFirmwareRelease(idRelease) {
        return call2(`/firmware-release?idRelease=${idRelease}`, null, {
            method: 'DELETE',
        });
    }

    function createNewFirmwareRelease(releaseData) {
        return call2('/firmware-release', releaseData, {
            method: 'POST',
        });
    }

    function editFirmwareRelease(releaseData) {
        return call2('/firmware-release', releaseData, {
            method: 'PATCH',
        });
    }

    function firmwares() {
        return call2('/firmware-upgrade', null, {
            method: 'GET',
        });
    }

    function uploadFirmwares(firmwareData) {
        return call2('/firmware-upgrade', firmwareData, {
            method: 'POST',
            skipResultChecks: true,
        });
    }

    function deviceNames(data) {
        return call2('/device-name', data, {
            method: 'GET',
        });
    }
}
